import { useState, useEffect, useCallback } from 'react';
import { fetchPolymarketStats, type PolymarketStatsResponse } from '../api/client';

interface PolymarketWidgetProps {
  className?: string;
  refreshInterval?: number; // Interval in ms, default 30000
}

/**
 * Get health status indicator based on circuit breaker status
 */
function getHealthStatus(stats: PolymarketStatsResponse): {
  status: 'healthy' | 'degraded' | 'unhealthy';
  label: string;
  color: string;
  bgColor: string;
} {
  if (stats.circuitBreakerStatus === 'open') {
    return {
      status: 'unhealthy',
      label: 'CIRCUIT OPEN',
      color: 'text-danger',
      bgColor: 'bg-danger',
    };
  }

  if (stats.circuitBreakerStatus === 'half-open') {
    return {
      status: 'degraded',
      label: 'RECOVERING',
      color: 'text-warning',
      bgColor: 'bg-warning',
    };
  }

  if (!stats.gammaHealthy || !stats.clobHealthy) {
    return {
      status: 'degraded',
      label: 'DEGRADED',
      color: 'text-warning',
      bgColor: 'bg-warning',
    };
  }

  if (stats.errorCountLastHour > 5) {
    return {
      status: 'degraded',
      label: 'ELEVATED ERRORS',
      color: 'text-warning',
      bgColor: 'bg-warning',
    };
  }

  return {
    status: 'healthy',
    label: 'HEALTHY',
    color: 'text-success',
    bgColor: 'bg-success',
  };
}

/**
 * Format relative time from ISO timestamp
 */
function formatRelativeTime(isoString: string): string {
  const now = new Date();
  const then = new Date(isoString);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return 'just now';
  if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
  if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)}h ago`;
  return `${Math.floor(diffSecs / 86400)}d ago`;
}

/**
 * Format number with commas
 */
function formatNumber(n: number): string {
  return n.toLocaleString();
}

export default function PolymarketWidget({ className = '', refreshInterval = 30000 }: PolymarketWidgetProps) {
  const [stats, setStats] = useState<PolymarketStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchPolymarketStats();
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('[PolymarketWidget] Failed to fetch stats:', err);
      setError('Failed to load Polymarket stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load immediately
    loadStats();

    // Set up periodic refresh
    const interval = setInterval(loadStats, refreshInterval);
    return () => clearInterval(interval);
  }, [loadStats, refreshInterval]);

  if (loading && !stats) {
    return (
      <div className={`card ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 w-32 bg-surface-50 rounded mb-3"></div>
          <div className="h-6 w-20 bg-surface-50 rounded mb-2"></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="h-16 bg-surface-50 rounded"></div>
            <div className="h-16 bg-surface-50 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className={`card ${className}`}>
        <div className="text-sm text-gray-400">Polymarket API</div>
        <div className="text-danger text-sm mt-2">{error}</div>
        <button
          onClick={loadStats}
          className="mt-2 text-xs text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stats) return null;

  const health = getHealthStatus(stats);

  return (
    <div className={`card ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">Polymarket API</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${health.bgColor}`}></div>
          <span className={`text-xs font-medium ${health.color}`}>{health.label}</span>
        </div>
      </div>

      {/* API Calls Today */}
      <div className="mb-4">
        <div className="text-xs text-gray-500 mb-2">API Calls Today</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-surface-50 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">Gamma</div>
            <div className="text-xl font-bold">{formatNumber(stats.gammaCallsToday)}</div>
            <div className="text-xs text-gray-500">{stats.gammaCallsThisMinute}/min</div>
          </div>
          <div className="bg-surface-50 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">CLOB</div>
            <div className="text-xl font-bold">{formatNumber(stats.clobCallsToday)}</div>
            <div className="text-xs text-gray-500">{stats.clobCallsThisMinute}/min</div>
          </div>
        </div>
      </div>

      {/* Events Tracked */}
      <div className="mb-4">
        <div className="text-xs text-gray-500 mb-2">Events Tracked</div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-danger"></div>
            <span className="text-sm">Live: <span className="font-medium">{stats.liveEvents}</span></span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-warning"></div>
            <span className="text-sm">Pre-game: <span className="font-medium">{stats.preGameEvents}</span></span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm">Total: <span className="font-medium">{stats.eventsTracked}</span></span>
          </div>
        </div>
      </div>

      {/* Performance */}
      <div className="mb-4">
        <div className="text-xs text-gray-500 mb-2">Performance</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-surface-50 rounded-lg p-2">
            <div className="text-xs text-gray-400 mb-1">Avg Response</div>
            <div className={`font-medium ${stats.avgResponseTimeMs > 2000 ? 'text-warning' : stats.avgResponseTimeMs > 5000 ? 'text-danger' : ''}`}>
              {stats.avgResponseTimeMs > 0 ? `${stats.avgResponseTimeMs}ms` : 'N/A'}
            </div>
          </div>
          <div className="bg-surface-50 rounded-lg p-2">
            <div className="text-xs text-gray-400 mb-1">Errors (1hr)</div>
            <div className={`font-medium ${stats.errorCountLastHour > 5 ? 'text-warning' : stats.errorCountLastHour > 10 ? 'text-danger' : ''}`}>
              {stats.errorCountLastHour}
            </div>
          </div>
        </div>
      </div>

      {/* API Health Details */}
      <div className="mb-3">
        <div className="text-xs text-gray-500 mb-2">API Health</div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${stats.gammaHealthy ? 'bg-success' : 'bg-danger'}`}></div>
            <span className={stats.gammaHealthy ? 'text-gray-300' : 'text-danger'}>Gamma</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${stats.clobHealthy ? 'bg-success' : 'bg-danger'}`}></div>
            <span className={stats.clobHealthy ? 'text-gray-300' : 'text-danger'}>CLOB</span>
          </div>
        </div>
      </div>

      {/* Last Update */}
      <div className="text-xs text-gray-500 text-right">
        Last poll: {formatRelativeTime(stats.lastPoll)}
      </div>

      {/* Circuit Breaker Warning */}
      {stats.circuitBreakerStatus === 'open' && (
        <div className="mt-3 p-2 bg-danger/10 border border-danger/30 rounded-lg text-xs text-danger">
          Circuit breaker is open! API calls are temporarily blocked to prevent further errors.
          {stats.circuitBreaker.gamma.cooldownRemaining && (
            <span> Gamma cooldown: {stats.circuitBreaker.gamma.cooldownRemaining}s</span>
          )}
          {stats.circuitBreaker.clob.cooldownRemaining && (
            <span> CLOB cooldown: {stats.circuitBreaker.clob.cooldownRemaining}s</span>
          )}
        </div>
      )}
      {stats.circuitBreakerStatus === 'half-open' && (
        <div className="mt-3 p-2 bg-warning/10 border border-warning/30 rounded-lg text-xs text-warning">
          Circuit breaker is testing recovery. Limited requests are being allowed.
        </div>
      )}
      {stats.errorCountLastHour > 10 && stats.circuitBreakerStatus === 'closed' && (
        <div className="mt-3 p-2 bg-warning/10 border border-warning/30 rounded-lg text-xs text-warning">
          High error rate detected ({stats.errorCountLastHour} errors in the last hour).
        </div>
      )}
    </div>
  );
}

/**
 * Compact version of the Polymarket widget for use in headers or sidebars
 */
export function PolymarketWidgetCompact({ className = '', refreshInterval = 30000 }: PolymarketWidgetProps) {
  const [stats, setStats] = useState<PolymarketStatsResponse | null>(null);

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchPolymarketStats();
      setStats(data);
    } catch (err) {
      console.error('[PolymarketWidgetCompact] Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, refreshInterval);
    return () => clearInterval(interval);
  }, [loadStats, refreshInterval]);

  if (!stats) {
    return <div className="animate-pulse h-6 w-32 bg-surface-50 rounded"></div>;
  }

  const health = getHealthStatus(stats);

  return (
    <div className={`flex items-center gap-2 ${className}`} title={`Polymarket: ${stats.eventsTracked} events tracked`}>
      <div className={`w-2 h-2 rounded-full ${health.bgColor}`}></div>
      <span className="text-sm">
        <span className="text-gray-400">PM:</span>
        <span className="font-medium ml-1">{stats.eventsTracked}</span>
        <span className="text-gray-500 text-xs ml-1">events</span>
      </span>
    </div>
  );
}
